import type { WorkflowDefinition, WorkflowRunResponse } from '@/lib/api';
import type { DagNodeState, WorkflowTokenUsageSummary } from '@/lib/types';

export interface WorkflowBudgetWarning {
  nodeId: string;
  nodeName: string;
  actualCostUsd: number;
  maxBudgetUsd: number;
}

export interface WorkflowRunTrendPoint {
  runId: string;
  startedAt: string;
  durationMs: number;
  totalCostUsd: number;
  totalTokens: number;
}

export interface WorkflowObservabilitySummary {
  totalCostUsd: number;
  tokenUsage: WorkflowTokenUsageSummary;
  slowestNode: DagNodeState | null;
  costliestNode: DagNodeState | null;
  nodesWithCost: number;
  budgetWarnings: WorkflowBudgetWarning[];
}

function getMaxBudgetUsd(
  workflowDefinition: WorkflowDefinition | null | undefined,
  nodeId: string
): number | null {
  const node = workflowDefinition?.nodes?.find(candidate => candidate.id === nodeId);
  if (!node || !('maxBudgetUsd' in node)) return null;
  return typeof node.maxBudgetUsd === 'number' ? node.maxBudgetUsd : null;
}

function normalizeNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function normalizeWorkflowTokenUsage(value: unknown): WorkflowTokenUsageSummary | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const input = normalizeNumber(record.input);
  const output = normalizeNumber(record.output);
  const total = normalizeNumber(record.total) || input + output;
  if (input === 0 && output === 0 && total === 0) return undefined;
  return { input, output, total };
}

function deriveWorkflowTokenUsageFromDagNodes(
  _dagNodes: DagNodeState[]
): WorkflowTokenUsageSummary {
  return { input: 0, output: 0, total: 0 };
}

export function buildWorkflowObservabilitySummary(
  dagNodes: DagNodeState[],
  workflowDefinition?: WorkflowDefinition | null,
  totalCostUsd?: number,
  tokenUsage?: WorkflowTokenUsageSummary
): WorkflowObservabilitySummary {
  const nodesWithCost = dagNodes.filter(node => typeof node.costUsd === 'number');
  const derivedTotalCostUsd = nodesWithCost.reduce((sum, node) => sum + (node.costUsd ?? 0), 0);
  const effectiveTotalCostUsd =
    typeof totalCostUsd === 'number' && Number.isFinite(totalCostUsd)
      ? totalCostUsd
      : derivedTotalCostUsd;

  const slowestNode = dagNodes.reduce<DagNodeState | null>((slowest, node) => {
    if (typeof node.duration !== 'number') return slowest;
    if (!slowest || (slowest.duration ?? -1) < node.duration) return node;
    return slowest;
  }, null);

  const costliestNode = dagNodes.reduce<DagNodeState | null>((costliest, node) => {
    if (typeof node.costUsd !== 'number') return costliest;
    if (!costliest || (costliest.costUsd ?? -1) < node.costUsd) return node;
    return costliest;
  }, null);

  const budgetWarnings = dagNodes.flatMap(node => {
    if (typeof node.costUsd !== 'number') return [];
    const maxBudgetUsd = getMaxBudgetUsd(workflowDefinition, node.nodeId);
    if (maxBudgetUsd === null || node.costUsd <= maxBudgetUsd) return [];
    return [
      {
        nodeId: node.nodeId,
        nodeName: node.name,
        actualCostUsd: node.costUsd,
        maxBudgetUsd,
      },
    ];
  });

  return {
    totalCostUsd: effectiveTotalCostUsd,
    tokenUsage: tokenUsage ?? deriveWorkflowTokenUsageFromDagNodes(dagNodes),
    slowestNode,
    costliestNode,
    nodesWithCost: nodesWithCost.length,
    budgetWarnings,
  };
}

export function buildWorkflowRunTrendSeries(
  runs: WorkflowRunResponse[],
  workflowName: string,
  currentRunId?: string
): WorkflowRunTrendPoint[] {
  return runs
    .filter(run => {
      if (run.workflow_name !== workflowName) return false;
      if (run.id === currentRunId) return false;
      return run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled';
    })
    .map(run => {
      const completedAt =
        typeof run.completed_at === 'string' ? Date.parse(run.completed_at) : Number.NaN;
      const startedAt = Date.parse(run.started_at);
      const tokenUsage = normalizeWorkflowTokenUsage(run.metadata?.token_usage);
      return {
        runId: run.id,
        startedAt: run.started_at,
        durationMs:
          Number.isFinite(startedAt) && Number.isFinite(completedAt)
            ? Math.max(0, completedAt - startedAt)
            : 0,
        totalCostUsd: normalizeNumber(run.metadata?.total_cost_usd),
        totalTokens: tokenUsage?.total ?? 0,
      };
    })
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt))
    .slice(-8);
}

export function buildSparklinePath(
  values: number[],
  width: number,
  height: number,
  padding = 2
): string {
  if (values.length === 0) return '';
  if (values.length === 1) {
    const y = height / 2;
    return `M ${padding} ${y} L ${width - padding} ${y}`;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  return values
    .map((value, index) => {
      const x = padding + (index / (values.length - 1)) * (width - padding * 2);
      const y = height - padding - ((value - min) / range) * (height - padding * 2);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
}
